import type { DokumentArt, DokumentGruppe, DokumentLieferant, MitgeltendesDokument } from './types';

/**
 * Katalog der mitgeltenden Dokumente.
 *
 * ─── Wie die Arten verteilt sind ─────────────────────────────────────────
 * Art C (allgemeine Arbeitgeberpflicht) steht nur fuer die Checkliste im
 * Katalog; keine Regel schlaegt sie vor. Einige Dokumente, die dem Namen
 * nach wie Art C klingen, sind bewusst Art B, weil sie an DIESER Baustelle
 * selbst die Sicherheitsgrundlage sind oder eine gemeinsam genutzte
 * Einrichtung freigeben:
 *  - Aufbau- und Verwendungsanleitung (Geruest, Schalung, Seitenschutz),
 *  - Betriebsanleitung mit Traglasttabelle (Bagger/Lader als Hebezeug),
 *  - Verlegeanleitung des Rohrherstellers, Verwendungsanleitung Verbaugeraet,
 *  - Gefaehrdungsbeurteilung nach TRGS 559 bzw. 553 (Staub, stoffbezogen),
 *  - Pruefnachweise NACH dem Aufbau am Aufstellort (Geruest, Kran,
 *    Baustromverteiler, Seitenschutz, Tankanlage) — die wiederkehrende
 *    Pruefung jedes Arbeitsmittels bleibt Art C,
 *  - stoff- bzw. anlagenbezogene Betriebsanweisungen, die das Regelwerk
 *    gesondert verlangt (Gefahrstoffe, Biostoffe, Fluessiggas,
 *    Stromerzeuger, Laser) — die allgemeine Betriebsanweisung fuer
 *    Arbeitsmittel bleibt Art C.
 *
 * ─── Belege ──────────────────────────────────────────────────────────────
 * `beleg: 'MK'` heisst: im Maengelkatalog der App (gesamtkatalog.json) steht
 * ein Eintrag mit dieser Dokumentpflicht; `rechtsgrundlage` zitiert dann die
 * dort genannte Vorschrift. `beleg: 'praxis'` ist Fachpraxis und vor einer
 * Freigabe fachlich zu pruefen.
 *
 * Reihenfolge = Reihenfolge der Vorschlaege im Plan. Ids nie umbenennen.
 */

/** Fassung des Katalogs. Hochzaehlen, wenn sich Ids oder Inhalte aendern. */
export const KATALOG_VERSION = 1;

export const GRUPPEN_TITEL: Record<DokumentGruppe, string> = {
    bestand: 'Vorbereitung und Bestand',
    tiefbau: 'Erd-, Tief- und Spezialtiefbau',
    rohbau: 'Rohbau und Montage',
    geruest: 'Gerüste und Absturz',
    hebezeug: 'Hebezeuge und Fördertechnik',
    schadstoff: 'Abbruch, Schadstoffe, Stäube',
    brand_energie: 'Brand, Energie, Sonderverfahren',
    verkehr_wasser: 'Verkehr, Wasser, Bahn',
    allgemein: 'Allgemeine Pflichten des Arbeitgebers',
};

export const ART_TITEL: Record<DokumentArt, string> = {
    A: 'Unterlage von Bauherr, Planer oder Gutachter',
    B: 'Nachweis des ausführenden Unternehmens vor Beginn',
    C: 'Allgemeine Pflicht des Arbeitgebers',
};

export const LIEFERANT_TITEL: Record<DokumentLieferant, string> = {
    BH: 'Bauherr',
    OP: 'Objektplaner',
    TWP: 'Tragwerksplaner',
    TGA: 'Fachplaner TGA',
    FP: 'Fachplaner',
    GUT: 'Gutachter',
    BEH: 'Behörde / Versorger',
    AN: 'Ausführendes Unternehmen',
    BET: 'Betreiber',
};

// Wiederkehrende Kurzzitate (Wortlaut wie im Maengelkatalog).
const DGUV_I_SEITENSCHUTZ = 'DGUV Information „Einsatz von Seitenschutz und Seitenschutzsystemen sowie Randsicherungen als Schutzvorrichtungen bei Bauarbeiten“';
const DGUV_R_SPEZIALTIEFBAU = 'DGUV Regel „Arbeiten im Spezialtiefbau“';

export const DOKUMENTE: MitgeltendesDokument[] = [
    // ═══ Vorbereitung und Bestand ═══════════════════════════════════════════
    {
        id: 'baustellenordnung',
        name: 'Baustellenordnung',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BH'],
        wannBenoetigt: 'Auf Baustellen mit mehreren Arbeitgebern, sobald gemeinsame Regeln für Zugang, Ordnung, Verkehr, Brandschutz und Verhalten gelten sollen; sie wird vom Bauherrn bzw. in seinem Auftrag erstellt und Vertragsbestandteil.',
        sigekoRelevanz: 'Mit dem SiGe-Plan abstimmen, damit beide dieselben Regeln enthalten, allen Auftragnehmern mit dem Vertrag zukommen lassen und ihre Kernpunkte in die Baustelleneinweisung übernehmen.',
        beleg: 'praxis',
        merkmale: ['mehrereArbeitgeber'],
        alias: ['BaustellenO'],
    },
    {
        id: 'be_plan',
        name: 'Baustelleneinrichtungsplan',
        kurz: 'BE-Plan',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['OP', 'AN'],
        wannBenoetigt: 'Für jede Baustelle mit gemeinsam genutzten Flächen: Zufahrten, Verkehrswege, Lagerplätze, Kran- und Gefahrbereiche, Sozialeinrichtungen sowie Erste-Hilfe- und Sammelplätze werden darin festgelegt und je Bauphase fortgeschrieben.',
        sigekoRelevanz: 'Vom Objektplaner bzw. Generalunternehmer anfordern, auf Trennung von Fahr- und Fußwegen, Kranschwenkbereiche, Abstände zu Baugruben sowie Flucht- und Rettungswege prüfen und als Anlage zum SiGe-Plan führen.',
        beleg: 'praxis',
        merkmale: ['mehrereArbeitgeber'],
        alias: ['Baustelleneinrichtungsplanung'],
    },
    {
        id: 'werksvorschriften',
        name: 'Werksvorschriften des Betreibers',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BET'],
        wannBenoetigt: 'Wenn auf einem Werks- oder Betriebsgelände oder in einem Gebäude gebaut wird, dessen Betrieb weiterläuft; der Betreiber regelt darin Zutritt, Verhalten, Alarmierung und betriebliche Gefahren.',
        sigekoRelevanz: 'Beim Betreiber anfordern, auf Widersprüche zum SiGe-Plan prüfen (Fahrwege, Rauchverbot, Sammelplätze) und die baurelevanten Regeln mit dem Ansprechpartner des Betreibers in SiGe-Plan und Baustelleneinweisung übernehmen.',
        rechtsgrundlage: ['ArbSchG § 8', 'DGUV V 1 § 6'],
        beleg: 'praxis',
        merkmale: ['betriebLaeuftWeiter'],
        alias: ['Werksvorschriften', 'Werksvorschrift', 'Betriebsvorschriften des Betreibers'],
    },
    {
        id: 'erlaubnisscheinverfahren',
        name: 'Erlaubnisscheinverfahren des Betreibers',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BET'],
        wannBenoetigt: 'Wenn der Betreiber Arbeiten in seinen Anlagen nur mit schriftlicher Freigabe zulässt, typisch für feuergefährliche Arbeiten, Eingriffe in Leitungen und Anlagen sowie das Betreten abgesperrter oder explosionsgefährdeter Bereiche.',
        sigekoRelevanz: 'Verfahren, Formulare und ausstellende Stellen beim Betreiber erfragen, im SiGe-Plan festlegen, welche Tätigkeiten einen Erlaubnisschein brauchen und wer ihn beantragt, und die Vorlaufzeiten des Betreibers in den Bauablauf einplanen.',
        rechtsgrundlage: ['ArbSchG § 8', 'DGUV V 1 § 6'],
        beleg: 'praxis',
        alias: ['Erlaubnisschein', 'Arbeitserlaubnisschein', 'Work-Permit'],
    },
    {
        id: 'abgrenzungsvereinbarung',
        name: 'Abgrenzungsvereinbarung mit dem Betreiber',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BH', 'BET'],
        wannBenoetigt: 'Wenn Beschäftigte des Betreibers und der Baufirmen im selben Bereich arbeiten oder Anlagen des Betreibers in Betrieb bleiben; Zuständigkeiten, Schnittstellen und Weisungsrechte werden schriftlich abgegrenzt.',
        sigekoRelevanz: 'Beim Bauherrn anfordern bzw. anstoßen; die Abgrenzung (wer schaltet, wer sperrt, wer alarmiert) in SiGe-Plan und BE-Plan übernehmen und bei Änderungen des Bauablaufs fortschreiben.',
        rechtsgrundlage: ['ArbSchG § 8', 'DGUV V 1 § 6'],
        beleg: 'praxis',
        merkmale: ['betriebLaeuftWeiter'],
        alias: ['Schriftliche Vereinbarung mit dem Betreiber'],
    },
    {
        id: 'explosionsschutzdokument',
        name: 'Explosionsschutzdokument des Betreibers',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BET'],
        wannBenoetigt: 'Wenn in oder neben explosionsgefährdeten Bereichen gearbeitet wird (Tankstellen, Chemie- und Gasanlagen, Lösemittellager); es weist die Ex-Zonen und die dort zulässigen Arbeitsmittel aus.',
        sigekoRelevanz: 'Beim Betreiber anfordern, Ex-Zonen in BE-Plan und SiGe-Plan übertragen und festlegen, dass Schweißen, Trennschleifen und nicht ex-geschützte Geräte dort nur mit Erlaubnisschein und Freimessung zulässig sind.',
        rechtsgrundlage: ['GefStoffV § 6', 'TRGS 720'],
        beleg: 'praxis',
        ufsa: true,
        alias: ['Ex-Schutzdokument', 'Ex-Zonenplan'],
    },
    {
        id: 'hygieneplan',
        name: 'Hygieneplan der Einrichtung (Bauen im laufenden Betrieb)',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BET'],
        wannBenoetigt: 'Bei Arbeiten in Krankenhäusern, Kliniken, Pflegeeinrichtungen und Laboren, deren Betrieb weiterläuft; die Einrichtung legt darin Schutzzonen, Staubschutz, Schleusen und Reinigung fest.',
        sigekoRelevanz: 'Über die Hygienefachkraft anfordern, Schutzzonen, Staubschutzwände und Wegeführung in BE-Plan und SiGe-Plan übernehmen und umgekehrt Infektionsgefahren für die Beschäftigten (Isolierbereiche, Laborabfälle) in die Einweisung aufnehmen.',
        rechtsgrundlage: ['IfSG § 23 (5)'],
        beleg: 'praxis',
        alias: ['Hygieneplan'],
    },
    {
        id: 'leitungsauskunft',
        name: 'Leitungsauskunft mit Bestandsplänen der Versorger',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BH', 'BEH'],
        wannBenoetigt: 'Vor jedem Eingriff in den Boden (Aushub, Aufgrabung, Bohrung, Rammung); einzuholen bei allen Netzbetreibern für Strom, Gas, Wasser, Fernwärme und Telekommunikation und auf der Baustelle vorzuhalten.',
        sigekoRelevanz: 'Prüfen, ob die Auskünfte aller Sparten vorliegen und aktuell sind, Trassen in BE-Plan und SiGe-Plan eintragen und mit den Tiefbaugewerken Suchschlitze bzw. Handschachtung im Leitungsbereich festlegen.',
        rechtsgrundlage: ['DGUV V 38 § 6 (1)', 'DGUV R 101-038 Abschn. 6.1'],
        beleg: 'MK',
        ufsa: true,
        merkmale: ['erdarbeiten'],
        alias: ['Leitungsauskunft', 'Spartenauskunft', 'Bestandspläne der Versorger', 'Bestands- / Leitungspläne'],
    },
    {
        id: 'schutzanweisung_leitungsbetreiber',
        name: 'Vorgaben und Schutzanweisung des Leitungsbetreibers',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BEH'],
        wannBenoetigt: 'Wenn im Bereich von Versorgungsleitungen gearbeitet wird; der Netzbetreiber legt darin Schutzabstände, zulässige Arbeitsverfahren und Meldepflichten fest, vielerorts als Schachtschein.',
        sigekoRelevanz: 'Mit der Leitungsauskunft anfordern, Auflagen (Handschachtung, Mindestabstände, Anwesenheit des Netzbetreibers, Schadensmeldung) in den SiGe-Plan übernehmen und den Tiefbaugewerken vor Beginn nachweislich übergeben.',
        rechtsgrundlage: ['DGUV V 38 § 6 (1)'],
        beleg: 'MK',
        merkmale: ['erdarbeiten'],
        alias: [
            'Vorgaben des Leitungsbetreibers',
            'Schutzanweisung des Leitungsbetreibers',
            'Schachtschein',
            'Vorschriften EVU',
            'Vorschr. Stadtwerke',
            'Kabelschutzanweisung',
        ],
    },
    {
        id: 'leitungsplaene_gebaeude',
        name: 'Bestands- und Leitungspläne der Gebäudetechnik',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BH', 'TGA', 'BET'],
        wannBenoetigt: 'Vor Kernbohrungen, Durchbrüchen, Schlitz- und Stemmarbeiten im Bestand, damit verdeckte Elektro-, Gas-, Wasser- und Heizungsleitungen nicht angebohrt werden.',
        sigekoRelevanz: 'Beim Bauherrn bzw. Betreiber anfordern; wo Pläne fehlen oder unsicher sind, im SiGe-Plan Ortung bzw. Freischaltung vor dem Bohren festlegen und die Zuständigkeit dafür einem Gewerk zuweisen.',
        rechtsgrundlage: ['DGUV V 38 § 6 (1)'],
        beleg: 'MK',
        ufsa: true,
        merkmale: ['bestand'],
        alias: ['Leitungspläne Gebäude', 'Bestandspläne Haustechnik', 'Revisionspläne TGA'],
    },
    {
        id: 'freigabe_tragwerksplaner',
        name: 'Freigabe des Tragwerksplaners für Durchbrüche und Schlitze',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['TWP'],
        wannBenoetigt: 'Vor Kernbohrungen, Durchbrüchen, Schlitz- und Stemmarbeiten in tragenden Bauteilen; Lage und Größe müssen vom Tragwerksplaner geprüft und freigegeben sein.',
        sigekoRelevanz: 'Freigabe bzw. Durchbruchsplan vor Beginn vorliegen lassen, nicht freigegebene Eingriffe in tragende Bauteile im SiGe-Plan ausdrücklich ausschließen und geforderte Abstützungen als Voraussetzung festhalten.',
        rechtsgrundlage: ['DGUV V 38 § 5 (1)', 'DGUV R 101-038 Abschn. 5.1'],
        beleg: 'MK',
        alias: ['Durchbruchsfreigabe', 'Durchbruchsplan'],
    },
    {
        id: 'freischaltung_netzbetreiber',
        name: 'Freischaltbestätigung bzw. Abstimmung mit dem Netzbetreiber',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BEH', 'BH'],
        wannBenoetigt: 'Wenn in der Nähe von Freileitungen, Fahr- oder Oberleitungen gearbeitet wird und Kran, Bagger, Gerüst oder Betonpumpe die Schutzabstände unterschreiten können.',
        sigekoRelevanz: 'Früh über den Bauherrn beim Netzbetreiber klären, ob freigeschaltet wird oder welche Schutzmaßnahmen (Abschrankung, Abdeckung, Arbeitsbereichsbegrenzung) gelten; Ergebnis mit Zeitraum und Ansprechpartner in SiGe-Plan und BE-Plan eintragen und den Geräteeinsatz danach koordinieren.',
        rechtsgrundlage: ['DGUV V 38 § 6 (2)', 'DGUV R 101-038 Abschn. 6.2'],
        beleg: 'MK',
        merkmale: ['freileitung'],
        alias: ['Freischaltbestätigung', 'Abstimmung Netzbetreiber'],
    },
    {
        id: 'pv_abschaltkonzept',
        name: 'Abschaltkonzept der Photovoltaikanlage',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BET', 'TGA'],
        wannBenoetigt: 'Bei Arbeiten auf, an oder unter bestehenden PV-Anlagen (Dachsanierung, Brandschutz, Abbruch); die Gleichstromseite steht bei Lichteinfall unter Spannung und lässt sich nicht einfach abschalten.',
        sigekoRelevanz: 'Beim Betreiber anfordern, die Grenzen der Abschaltung (spannungsführende DC-Leitungen und Module) in SiGe-Plan und Einweisung aufnehmen und festlegen, wer abschaltet, abdeckt oder demontiert und den Bereich gegen Betreten sichert.',
        rechtsgrundlage: ['DGUV V 38 § 6 (2)'],
        beleg: 'MK',
        ufsa: true,
        alias: ['Abschaltkonzept PV'],
    },
    {
        id: 'freigabe_gasnetzbetreiber',
        name: 'Arbeitsanweisung und Freigabe des Gasnetzbetreibers',
        art: 'B',
        gruppe: 'bestand',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Bei Arbeiten an oder in Gasleitungen und bei Aufgrabungen an Leitungen unter Gasdruck; Arbeitsverfahren, Absperrung und Aufsicht müssen vor Beginn mit dem Netzbetreiber festgelegt sein.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob Aufsichtführender, Gasprüfung, Brandbekämpfung und Fluchtwege geregelt sind; Gefahrbereich und Zündquellenverbot mit den übrigen Gewerken koordinieren.',
        rechtsgrundlage: ['DGUV R 100-500 Kap. 2.31'],
        beleg: 'MK',
    },
    {
        id: 'kampfmittelauskunft',
        name: 'Kampfmittelauskunft (Luftbildauswertung)',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BH', 'BEH'],
        wannBenoetigt: 'Vor Erdarbeiten, Bohrungen und Rammarbeiten auf Flächen, für die ein Kampfmittelverdacht nicht ausgeschlossen ist; in vielen Ländern über den Kampfmittelbeseitigungsdienst einzuholen.',
        sigekoRelevanz: 'Beim Bauherrn anfordern; bei Verdacht Sondierung bzw. Kampfmittelbegleitung vor Beginn der Erd- und Spezialtiefbauarbeiten einplanen und die Verdachtsflächen im BE-Plan kennzeichnen.',
        beleg: 'praxis',
        merkmale: ['erdarbeiten'],
        alias: ['Kampfmittelverdachtsflächenbewertung', 'Ermittlung der Kampfmittelverdachtsflächen'],
    },
    {
        id: 'kampfmittelfreigabe',
        name: 'Kampfmittelfreigabebescheinigung',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BEH', 'GUT'],
        wannBenoetigt: 'Wenn für die Baufläche ein Kampfmittelverdacht besteht: Nach Sondierung bzw. Räumung bescheinigt der Räumdienst, welche Flächen bis zu welcher Tiefe freigegeben sind.',
        sigekoRelevanz: 'Vor Beginn der Erdarbeiten vorlegen lassen, freigegebene Flächen und Tiefen mit dem geplanten Aushub abgleichen und Arbeiten außerhalb der Freigabe nur mit Kampfmittelbegleitung zulassen.',
        beleg: 'praxis',
        alias: ['Kampfmittelfreigabe', 'Freigabebescheinigung Kampfmittel'],
    },
    {
        id: 'standsicherheit_nachbarbebauung',
        name: 'Standsicherheitsnachweis für Nachbargebäude (DIN 4123)',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['TWP'],
        wannBenoetigt: 'Wenn neben bestehenden Gebäuden ausgeschachtet, gegründet oder unterfangen wird und die Ausführung nicht in den Grenzen der DIN 4123 ohne rechnerischen Nachweis bleibt; dazu gehören die bautechnischen Unterlagen (Abschnittsfolge, Unterfangungspläne).',
        sigekoRelevanz: 'Beim Tragwerksplaner anfordern, Abschnittsfolge und Arbeitstakte der Unterfangung in den Bauablauf übernehmen und kontrollieren lassen, dass danach gearbeitet wird; Nutzer des Nachbargebäudes über Gefahrbereiche informieren.',
        rechtsgrundlage: ['ArbSchG § 4', 'DIN 4123 Abschn. 1 und 10'],
        beleg: 'MK',
        alias: ['Standsicherheitsnachweis Nachbarbebauung', 'Unterfangungsplanung'],
    },
    {
        id: 'beweissicherung',
        name: 'Beweissicherung an Nachbargebäuden',
        art: 'A',
        gruppe: 'bestand',
        lieferant: ['BH', 'GUT'],
        wannBenoetigt: 'Vor Abbruch-, Ramm-, Rüttel- und Aushubarbeiten neben bestehender Bebauung; der Zustand (Risse, Setzungen) wird vor Beginn dokumentiert, oft mit Gipsmarken oder Rissmonitoring.',
        sigekoRelevanz: 'Beim Bauherrn anstoßen und Messpunkte sowie Warnwerte, bei denen die Arbeiten zu unterbrechen sind, in den SiGe-Plan übernehmen; neue Risse sind ein Frühwarnzeichen für Standsicherheitsprobleme, die auch Beschäftigte gefährden.',
        beleg: 'praxis',
        alias: ['Beweissicherung / Gipsmarken', 'Beweissicherungsverfahren'],
    },

    // ═══ Erd-, Tief- und Spezialtiefbau ═════════════════════════════════════
    {
        id: 'baugrundgutachten',
        name: 'Baugrundgutachten',
        art: 'A',
        gruppe: 'tiefbau',
        lieferant: ['GUT', 'BH'],
        wannBenoetigt: 'Vor Erdarbeiten, Baugruben, Gräben, Gründungen und Spezialtiefbau; Bodenarten, Schichtung, Grundwasser und Tragfähigkeit bestimmen Böschungswinkel, Verbau und Gerätestandplätze.',
        sigekoRelevanz: 'Beim Bauherrn anfordern und die für die Sicherheit wichtigen Angaben (zulässige Böschungsneigung, Grundwasser, Tragfähigkeit des Planums für Kran und Großgerät, Hindernisse) in den SiGe-Plan übernehmen.',
        rechtsgrundlage: ['DIN 4124', 'DIN EN 1997-2'],
        beleg: 'praxis',
        merkmale: ['erdarbeiten', 'tunnelbau'],
        alias: ['Bodengutachten', 'Geotechnischer Bericht'],
    },
    {
        id: 'standsicherheit_boeschung',
        name: 'Standsicherheitsnachweis Böschung (DIN 4084)',
        art: 'A',
        gruppe: 'tiefbau',
        lieferant: ['GUT', 'TWP'],
        wannBenoetigt: 'Wenn Böschungen höher als 5 m sind, steiler als nach DIN 4124 zulässig angelegt werden oder durch Auflasten (Geräte, Fahrzeuge, Lager, Nachbarbebauung) zusätzlich belastet werden.',
        sigekoRelevanz: 'Beim Planer bzw. Geotechniker anfordern; nachgewiesene Böschungswinkel, Lastabstände und Lastgrenzen am Rand in SiGe-Plan und BE-Plan übernehmen und den Gewerken, die dort fahren oder lagern, verbindlich vorgeben.',
        rechtsgrundlage: ['DIN 4124 Abschn. 4.2.8', 'DIN 4084'],
        beleg: 'MK',
        alias: ['Böschungsnachweis'],
    },
    {
        id: 'standsicherheit_verbau',
        name: 'Standsicherheitsnachweis Verbau',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN', 'TWP'],
        wannBenoetigt: 'Für Baugruben- und Grabenverbau außerhalb der Regelausführung (Normverbau) nach DIN 4124 sowie für Spund- und Trägerbohlwände, und zwar für jeden Bauzustand einschließlich Auflasten aus Kran und Fahrzeugen.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob Aushubstufen, Anker- bzw. Steifenlagen, Rückbau und Auflasten aus Kran, Betonpumpe und Lagerflächen erfasst sind; zulässige Lasten und Abstände in den BE-Plan übernehmen.',
        rechtsgrundlage: ['DIN 4124 Abschn. 4.3.8', 'DIN 4124 Abschn. 8.2.11'],
        beleg: 'MK',
        alias: ['Verbaustatik', 'Baugrubenstatik'],
    },
    {
        id: 'verwendungsanleitung_verbaugeraet',
        name: 'Verwendungsanleitung des Herstellers für das Verbaugerät',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Beim Einsatz von Grabenverbaugeräten (Verbauboxen, Gleitschienen-, Dielenkammerverbau); sie legt Einsatzgrenzen, Ein- und Ausbaufolge und zulässige Tiefen fest und muss auf der Baustelle vorliegen.',
        sigekoRelevanz: 'Vorlage auf der Baustelle prüfen, mit Grabentiefen und Leitungskreuzungen abgleichen und Ein- und Ausbau des Verbaus mit Rohrverlegung und Verfüllung zeitlich koordinieren.',
        rechtsgrundlage: ['DIN 4124 Abschn. 5.2.3'],
        beleg: 'MK',
        alias: ['Verwendungsanleitung Verbaugerät'],
    },
    {
        id: 'wasserrechtliche_erlaubnis',
        name: 'Wasserrechtliche Erlaubnis für die Wasserhaltung',
        art: 'A',
        gruppe: 'tiefbau',
        lieferant: ['BH', 'BEH'],
        wannBenoetigt: 'Wenn Grundwasser abgesenkt, entnommen oder wieder eingeleitet wird; sie legt Absenkziel, Fördermengen und Einleitstellen fest.',
        sigekoRelevanz: 'Beim Bauherrn anfordern; aus Absenkziel und Pumpenbetrieb folgt, welche Bauzustände bei Ausfall der Wasserhaltung gefährdet sind (Grundbruch, Auftrieb, Böschungsrutschung) – Notstrom, Überwachung und Räumung der Baugrube im SiGe-Plan regeln.',
        rechtsgrundlage: ['WHG §§ 8, 9'],
        beleg: 'praxis',
    },
    {
        id: 'erkundungsgutachten',
        name: 'Erkundungsgutachten (historische und technische Erkundung)',
        art: 'A',
        gruppe: 'tiefbau',
        lieferant: ['GUT', 'BH'],
        wannBenoetigt: 'Vor Arbeiten in kontaminierten Bereichen (Altlasten, Altstandorte, belastete Bausubstanz); Art, Menge und Verteilung der Gefahr- und Biostoffe müssen vor Beginn ermittelt, dokumentiert und bewertet sein.',
        sigekoRelevanz: 'Beim Bauherrn anfordern, auf Nutzungsgeschichte, Probenahme und Bewertung für den Arbeitsschutz prüfen und als Grundlage des Arbeits- und Sicherheitsplans weitergeben; belastete Bereiche im BE-Plan als Schwarzbereiche kennzeichnen.',
        rechtsgrundlage: ['DGUV R 101-004 Abschn. 11.1', 'TRGS 524'],
        beleg: 'MK',
        merkmale: ['altlastenverdacht'],
        ufsa: true,
        asplan: 'grundlage',
        alias: ['Historische Erkundung', 'Altlastengutachten'],
    },
    {
        id: 'arbeits_sicherheitsplan',
        name: 'Arbeits- und Sicherheitsplan (kontaminierte Bereiche)',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN', 'FP'],
        wannBenoetigt: 'Vor Beginn jeder Arbeit in kontaminierten Bereichen nach DGUV R 101-004 bzw. TRGS 524, also auch bei PCB-, PAK-, Teer- und Holzschutzmittelbelastung; er fasst Gefährdungen, Schutzmaßnahmen, Schwarz-Weiß-Bereiche, Messungen und Notfallorganisation zusammen.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen (oder im A+S-Plan erstellen) und mit dem SiGe-Plan verzahnen: Schwarzbereiche, Personal- und Geräteschleusen, Zugänge und Notfallabläufe müssen mit den übrigen Gewerken abgestimmt sein.',
        rechtsgrundlage: ['DGUV R 101-004 Abschn. 8.3', 'TRGS 524'],
        beleg: 'MK',
        merkmale: ['altlastenverdacht'],
        alias: ['A+S-Plan', 'Arbeits- und Sicherheitsplan', 'Arbeits- und Sicherheitsplan TRGS 524'],
    },
    {
        id: 'anzeige_uvt_kontaminiert',
        name: 'Anzeige an den UVT (kontaminierte Bereiche)',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Bauarbeiten in kontaminierten Bereichen, schriftlich und mindestens vier Wochen vor Beginn.',
        sigekoRelevanz: 'Frist in den Bauzeitenplan übernehmen und Kopie der Anzeige vor Beginn vorlegen lassen; ohne fristgerechte Anzeige verschiebt sich der Beginn im kontaminierten Bereich.',
        vorlauf: '4 Wochen vor Beginn an den UVT',
        rechtsgrundlage: ['DGUV R 101-004 Abschn. 11.2.1'],
        beleg: 'MK',
        merkmale: ['altlastenverdacht'],
        asplan: 'anlage',
    },
    {
        id: 'sachkunde_koordinator_kontaminiert',
        name: 'Sachkundenachweis des Koordinators (kontaminierte Bereiche)',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN', 'BH'],
        wannBenoetigt: 'Wenn für Arbeiten in kontaminierten Bereichen ein Koordinator nach DGUV R 101-004 bestellt wird; er muss seine Sachkunde durch einen anerkannten Lehrgang nachweisen.',
        sigekoRelevanz: 'Bestellung und Sachkundenachweis vor Beginn vorlegen lassen (ist der SiGeKo selbst bestellt, den eigenen Nachweis beifügen), den Koordinator mit Erreichbarkeit in SiGe-Plan und A+S-Plan benennen und die Aufgaben gegeneinander abgrenzen.',
        rechtsgrundlage: ['DGUV R 101-004 Abschn. 5.1', 'DGUV R 101-004 Abschn. 5.2'],
        beleg: 'MK',
        asplan: 'anlage',
    },
    {
        id: 'standsicherheit_spezialtiefbaumaschine',
        name: 'Standsicherheitsberechnung der Spezialtiefbaumaschine',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Beim Einsatz von Bohr-, Ramm- und Schlitzwandgeräten; die Standsicherheit auf dem vorhandenen Arbeitsplanum muss für alle Betriebszustände rechnerisch nachgewiesen sein.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und mit dem Baugrundgutachten abgleichen (Tragfähigkeit der Arbeitsebene, Abstand zu Baugrubenkanten und Leitungen); Herstellung und Erhalt des Planums einem Gewerk zuweisen.',
        rechtsgrundlage: [DGUV_R_SPEZIALTIEFBAU + ' Abschn. 4.1.12.4'],
        beleg: 'MK',
        alias: ['Standsicherheitsnachweis Bohrgerät'],
    },
    {
        id: 'arbeitsanweisung_bewehrungskoerbe',
        name: 'Arbeitsanweisung für Transport und Einbau von Bewehrungskörben',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Bohrpfählen und Schlitzwänden, deren Bewehrungskörbe mit Hebezeugen aufgerichtet, gestoßen und eingebaut werden.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen; Anschlagpunkte, Aufrichtvorgang und Gefahrbereich des Hebevorgangs mit dem Kranbetrieb und den Gewerken im Umfeld koordinieren.',
        rechtsgrundlage: [DGUV_R_SPEZIALTIEFBAU],
        beleg: 'MK',
    },
    {
        id: 'erschuetterung',
        name: 'Erschütterungsprognose und -messung (DIN 4150)',
        art: 'A',
        gruppe: 'tiefbau',
        lieferant: ['GUT', 'BH'],
        wannBenoetigt: 'Bei Ramm-, Rüttel- und Verdichtungsarbeiten sowie Abbruch mit schwerem Gerät neben bestehender Bebauung, Leitungen oder empfindlichen Anlagen.',
        sigekoRelevanz: 'Beim Bauherrn anstoßen, Anhaltswerte und Messstellen in den SiGe-Plan übernehmen und festlegen, wer bei Überschreitung die Arbeiten unterbricht; Erschütterungen können Böschungen, Verbau und Nachbarbauteile destabilisieren.',
        rechtsgrundlage: ['DIN 4150-3'],
        beleg: 'praxis',
        alias: ['Erschütterungsmessung', 'Erschütterungsprognose'],
    },
    {
        id: 'befahrerlaubnis',
        name: 'Erlaubnisschein für das Befahren enger Räume',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN', 'BET'],
        wannBenoetigt: 'Vor dem Einsteigen in Kanäle, Schächte, Behälter, Silos und andere enge Räume, in denen Sauerstoffmangel, Gase oder Dämpfe auftreten können.',
        sigekoRelevanz: 'Verfahren vor Beginn festlegen lassen (wer stellt aus, wer misst, wer sichert von außen) und Arbeiten anderer Gewerke, die die Atmosphäre im Raum verändern (Abgase, Schweißen, Beschichten), während der Befahrung ausschließen.',
        rechtsgrundlage: ['DGUV R 113-004', 'DGUV R 103-003'],
        beleg: 'praxis',
        alias: ['Befahrerlaubnis', 'Befahrerlaubnisschein'],
    },
    {
        id: 'freimessprotokoll',
        name: 'Freimessprotokoll',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor dem Betreten von Schächten, Rohrleitungen, Behältern und engen Räumen; Sauerstoffgehalt sowie explosionsfähige und giftige Gase werden vor und während der Arbeit gemessen.',
        sigekoRelevanz: 'Messverfahren, Messgeräte und Häufigkeit im SiGe-Plan festhalten lassen und bei Begehungen prüfen, ob vor dem Einstieg gemessen und dokumentiert wird.',
        rechtsgrundlage: ['DGUV I 201-052 Abschn. 5.4.2.1', 'DGUV R 103-003 Abschn. 3 und 4'],
        beleg: 'MK',
        alias: ['Freimessung'],
    },
    {
        id: 'rettungskonzept_enge_raeume',
        name: 'Rettungskonzept für enge Räume',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Arbeiten in Schächten, Kanälen, Rohrleitungen und Behältern; Rettungsgerät, Sicherungsposten und Rettungsablauf müssen vor dem Einstieg festgelegt und geübt sein.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob die Rettung ohne Feuerwehr möglich ist oder deren Anfahrzeit reicht; Zufahrt und Aufstellfläche für die Rettung im BE-Plan freihalten.',
        rechtsgrundlage: ['DGUV R 103-003 Abschn. 6.1', 'DGUV I 201-052 Abschn. 5.8'],
        beleg: 'MK',
        asplan: 'anlage',
    },
    {
        id: 'verlegeanleitung_rohre',
        name: 'Verlegeanleitung des Rohrherstellers',
        art: 'B',
        gruppe: 'tiefbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Rohrleitungs- und Kanalbau; Zusammenziehen bzw. Zusammenschieben von Muffenrohren, Anschlagen und Absenken der Rohre sowie zulässige Hilfsmittel richten sich nach den Herstellerangaben.',
        sigekoRelevanz: 'Auf der Baustelle vorliegen lassen und Absenk- und Verlegevorgänge im Graben mit dem Verbau (Ein- und Ausbau, Steifen) und dem Geräteeinsatz am Grabenrand koordinieren.',
        rechtsgrundlage: ['DGUV I 201-052'],
        beleg: 'MK',
    },

    // ═══ Rohbau und Montage ═════════════════════════════════════════════════
    {
        id: 'ausfuehrungsstatik',
        name: 'Ausführungsstatik mit Schal- und Bewehrungsplänen',
        art: 'A',
        gruppe: 'rohbau',
        lieferant: ['TWP'],
        wannBenoetigt: 'Für Ortbetonarbeiten an Bodenplatten, Decken, Wänden und Stützen; sie legt Ausschalfristen, Hilfsstützungen und die zulässige Belastung junger Bauteile fest.',
        sigekoRelevanz: 'Beim Tragwerksplaner anfordern, Ausschal- und Abstützfristen sowie zulässige Lasten auf frischen Decken (Material, Geräte, Gerüste) in den SiGe-Plan übernehmen und mit den Folgegewerken abstimmen.',
        rechtsgrundlage: ['DGUV V 38 § 5 (1)', 'DGUV R 101-038 Abschn. 5.1'],
        beleg: 'MK',
        ufsa: true,
        alias: ['Statik', 'Tragwerksplanung', 'Schal- und Bewehrungspläne'],
    },
    {
        id: 'statik_belastete_konstruktion',
        name: 'Statischer Nachweis der belasteten Konstruktion',
        art: 'A',
        gruppe: 'rohbau',
        lieferant: ['TWP', 'AN'],
        wannBenoetigt: 'Wenn Krane, Betonpumpen, Bagger, Lkw, Silos, Container oder Materiallager auf Decken, Tiefgaragen, Brücken, Dächern oder anderen Bauwerken stehen oder darüber fahren.',
        sigekoRelevanz: 'Vor der Aufstellung vom Tragwerksplaner bestätigen lassen und Aufstellflächen, zulässige Achs- und Stützlasten sowie Lastverteilung oder Unterstützung in BE-Plan und SiGe-Plan eintragen; nur freigegebene Geräte zulassen.',
        rechtsgrundlage: ['DGUV V 38 § 5 (1)', 'DGUV R 101-038 Abschn. 5.1'],
        beleg: 'MK',
        alias: ['Nachweis der Deckentragfähigkeit'],
    },
    {
        id: 'montageanweisung',
        name: 'Montageanweisung',
        art: 'B',
        gruppe: 'rohbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Montage- und Demontagearbeiten mit besonderen sicherheitstechnischen Anforderungen: Fertigteile, Stahl- und Holzbau, Fassadenelemente und Elementfassaden, Aufzugsanlagen, Schalung und Traggerüste, Schutznetze, Krane.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob Montagereihenfolge, Hilfsabstützungen, Absturzsicherung, Anschlagmittel und Gefahrbereiche festgelegt sind; Bereiche unter der Montage und den Kranbetrieb räumlich und zeitlich von anderen Gewerken trennen.',
        rechtsgrundlage: ['DGUV V 38 § 4', 'DGUV V 52 § 21'],
        beleg: 'MK',
        merkmale: ['fertigteilmontage'],
        alias: ['Muster-MontageAnw', 'Montageanleitung', 'Demontageanweisung'],
    },
    {
        id: 'montage_gewicht_anschlag',
        name: 'Angaben zu Gewicht und Anschlagpunkten der Bauteile',
        art: 'B',
        gruppe: 'rohbau',
        lieferant: ['AN', 'TWP'],
        wannBenoetigt: 'Bei der Montage von Fertigteilen, Stahl- und Holzbauteilen, Fassadenelementen und Glas; Gewicht, Schwerpunkt, Anschlagpunkte und Transportlage müssen vor dem Heben bekannt sein.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und mit Tragfähigkeit und Ausladung des Krans abgleichen (Kranstandort im BE-Plan); bei Schwerteilen oder Tandemhub Hubplan und Gefahrbereich festlegen lassen.',
        rechtsgrundlage: ['DGUV V 38 § 5 (2)', 'DGUV R 101-038 Abschn. 5.2'],
        beleg: 'MK',
        merkmale: ['fertigteilmontage'],
        alias: ['Gewichtsangaben der Bauteile'],
    },
    {
        id: 'standsicherheit_traggeruest',
        name: 'Standsicherheitsnachweis Traggerüst',
        art: 'B',
        gruppe: 'rohbau',
        lieferant: ['AN', 'TWP'],
        wannBenoetigt: 'Für Traggerüste und Deckenunterstützungen, die nicht als Regelausführung bzw. nach geprüfter Typenstatik des Herstellers aufgebaut werden, sowie bei großen Höhen und Sonderlasten.',
        sigekoRelevanz: 'Vor dem Aufbau vorlegen lassen und Abnahme des Traggerüsts vor dem Betonieren sowie Ausrüstfristen mit dem Tragwerksplaner abgestimmt in den Bauablauf übernehmen.',
        rechtsgrundlage: ['DIN EN 12812'],
        beleg: 'praxis',
    },
    {
        id: 'plan_schutznetze',
        name: 'Plan für Auf-, Um- und Abbau der Schutznetze',
        art: 'B',
        gruppe: 'rohbau',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei der Montage von Schutz- und Auffangnetzen; Anschlagpunkte, Montageablauf und Sicherung der Monteure sind vor Beginn festzulegen.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, Netzmontage mit Rohbau- und Dachgewerken takten, Nachweis der Alterungsprüfung der Netze verlangen und Bereiche unter den Netzen von Lagerung freihalten.',
        rechtsgrundlage: ['DGUV R 101-011 Abschn. 2.6'],
        beleg: 'MK',
    },

    // ═══ Gerüste und Absturz ════════════════════════════════════════════════
    {
        id: 'auv',
        name: 'Aufbau- und Verwendungsanleitung',
        kurz: 'AuV',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Für jedes Gerüst, jede fahrbare Arbeitsbühne, jedes Seitenschutzsystem und jede Systemschalung bzw. jedes Traggerüstsystem; sie ist der Plan für Auf-, Um- und Abbau und muss auf der Baustelle vorliegen.',
        sigekoRelevanz: 'Vorlage prüfen und mit der Ausführung abgleichen (Verankerung, Belag, Lastklasse, Bekleidung); Abweichungen von der Regelausführung lösen einen Standsicherheitsnachweis aus. Nutzung durch andere Gewerke nur innerhalb der angegebenen Lastklasse zulassen.',
        rechtsgrundlage: ['BetrSichV § 6', 'TRBS 2121-1', 'DGUV I 201-011 Abschn. 3'],
        beleg: 'MK',
        merkmale: ['geruest'],
        alias: ['Aufbau- und Verwendungsanleitung des Herstellers', 'Aufbau- u. Verwendungsanleitung'],
    },
    {
        id: 'pruefprotokoll_geruest',
        name: 'Prüfprotokoll und Kennzeichnung des Gerüsts',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Nach jedem Auf- und Umbau eines Gerüsts und vor der Benutzung durch andere Gewerke; Prüfung durch eine befähigte Person, Kennzeichnung mit Lastklasse und Freigabe am Zugang.',
        sigekoRelevanz: 'Freigabe vor der Nutzung durch andere Gewerke kontrollieren und im SiGe-Plan festlegen, wer Gerüste prüft, wer Änderungen (entfernte Anker oder Beläge) melden muss und wie gesperrte Bereiche gekennzeichnet werden.',
        rechtsgrundlage: ['TRBS 2121-1', 'DGUV I 201-011 Abschn. 5'],
        beleg: 'MK',
        merkmale: ['geruest'],
        alias: ['Gerüstprüfprotokoll', 'Gerüstkennzeichnung'],
    },
    {
        id: 'standsicherheit_geruest',
        name: 'Standsicherheitsnachweis Gerüst außerhalb der Regelausführung',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Sonder-, Hänge-, Konsol- und Auslegergerüste, Gerüstüberbrückungen und alle Gerüste, die von der Aufbau- und Verwendungsanleitung abweichen, auch bei Bekleidung mit Planen oder Netzen, die die AuV nicht abdeckt.',
        sigekoRelevanz: 'Vor dem Aufbau zusammen mit dem Plan für Auf-, Um- und Abbau vorlegen lassen, Ankerkräfte und Verankerungsgrund mit dem Bauwerk abstimmen und die Nutzungsgrenzen in den SiGe-Plan übernehmen.',
        rechtsgrundlage: ['TRBS 2121-1 Nr. 4.1', 'DGUV I 201-011 Abschn. 3'],
        beleg: 'MK',
        alias: ['Brauchbarkeitsnachweis Gerüst', 'Gerüststatik'],
    },
    {
        id: 'pruefung_seitenschutz',
        name: 'Prüfdokumentation Seitenschutz und Randsicherung',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Nach der Montage von Seitenschutzsystemen und Randsicherungen und vor der Benutzung; ordnungsgemäße Montage und Funktion sind zu prüfen und zu dokumentieren.',
        sigekoRelevanz: 'Dokumentation stichprobenartig prüfen und im SiGe-Plan regeln, wer den Seitenschutz vorhält, wer ihn für Anlieferungen kurzzeitig öffnen darf und wer ihn danach wieder schließt.',
        rechtsgrundlage: [DGUV_I_SEITENSCHUTZ],
        beleg: 'MK',
    },
    {
        id: 'brauchbarkeit_dachschutzwand',
        name: 'Brauchbarkeitsnachweis Dachschutzwand',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Dachschutzwände einschließlich ihrer Halterungen, die als Absturzsicherung an geneigten Dächern eingesetzt werden.',
        sigekoRelevanz: 'Vor der Montage vorlegen lassen, Befestigung am Dachtragwerk mit Dachdecker bzw. Zimmerer abstimmen und die Dachschutzwand als gemeinsam genutzte Schutzeinrichtung im SiGe-Plan führen.',
        rechtsgrundlage: [DGUV_I_SEITENSCHUTZ + ' Abschn. 4.2'],
        beleg: 'MK',
    },
    {
        id: 'rettungskonzept_psaga',
        name: 'Rettungskonzept für Arbeiten mit PSAgA',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Immer wenn persönliche Schutzausrüstung gegen Absturz oder Seilzugangstechnik eingesetzt wird; eine aufgefangene Person muss in kurzer Zeit gerettet werden können.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob Rettungsgerät und unterwiesene Retter vor Ort sind und die Rettung nicht von der Feuerwehr abhängt; für wiederkehrende Arbeiten an der fertigen Anlage in die Unterlage für spätere Arbeiten übernehmen.',
        rechtsgrundlage: ['DGUV V 38 § 9 (4)', 'TRBS 2121-1 Nr. 4.2.4'],
        beleg: 'MK',
        ufsa: true,
        alias: ['Rettungskonzept PSAgA'],
    },
    {
        id: 'durchtrittsicherheit',
        name: 'Nachweis der Tragfähigkeit und Durchtrittsicherheit der Dachfläche',
        art: 'A',
        gruppe: 'geruest',
        lieferant: ['TWP', 'OP', 'BET'],
        wannBenoetigt: 'Vor Arbeiten auf Dächern, besonders auf Bestands- und Leichtdächern, Lichtkuppeln, Lichtbändern und PV-Anlagen; es muss bekannt sein, welche Flächen begehbar und belastbar sind.',
        sigekoRelevanz: 'Beim Bauherrn bzw. Tragwerksplaner anfordern, nicht durchtrittsichere Flächen im SiGe-Plan als Gefahrbereich mit Absperrung oder Abdeckung ausweisen, zulässige Lasten für Materialablage angeben und für Wartungsarbeiten in die Unterlage für spätere Arbeiten übernehmen.',
        rechtsgrundlage: ['DGUV V 38 § 8 (2)', 'DGUV I 201-054'],
        beleg: 'MK',
        ufsa: true,
        merkmale: ['dacharbeiten'],
        alias: ['Nachweis Durchtrittsicherheit', 'Nachweis Durchsturzsicherheit'],
    },
    {
        id: 'pruefnachweis_anschlageinrichtung',
        name: 'Prüfnachweis der Anschlageinrichtungen',
        art: 'A',
        gruppe: 'geruest',
        lieferant: ['BET', 'BH'],
        wannBenoetigt: 'Wenn fest eingebaute Anschlageinrichtungen (Einzelanschlagpunkte, Seilsysteme) auf Dächern oder an Fassaden für PSAgA genutzt werden sollen; sie dürfen nur mit gültiger Prüfung verwendet werden.',
        sigekoRelevanz: 'Beim Betreiber bzw. Bauherrn anfordern, nur geprüfte Anschlageinrichtungen im SiGe-Plan zur Nutzung freigeben und bei neu errichteten Anlagen Montage- und Prüfdokumentation in die Unterlage für spätere Arbeiten übernehmen.',
        rechtsgrundlage: ['DIN EN 795'],
        beleg: 'praxis',
        ufsa: true,
        merkmale: ['dacharbeiten'],
        alias: ['Prüfbuch Anschlageinrichtungen'],
    },
    {
        id: 'anzeige_haengende_arbeitsbuehne',
        name: 'Anzeige an den UVT (hängende Arbeitsbühne, Arbeitssitz)',
        art: 'B',
        gruppe: 'geruest',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor dem ersten Einsatz hängender Arbeitsbühnen oder handbetriebener Arbeitssitze auf der Baustelle, schriftlich mindestens 14 Tage vorher.',
        sigekoRelevanz: 'Frist in den Bauablauf übernehmen und Kopie der Anzeige vorlegen lassen; Gefahrbereich unter Bühne bzw. Arbeitssitz und Rettung innerhalb von 15 Minuten mit den übrigen Gewerken koordinieren.',
        vorlauf: '14 Tage vor dem ersten Einsatz an den UVT',
        rechtsgrundlage: [
            'DGUV Information „Handbetriebene Arbeitssitze“ Abschn. 7.1.5',
            'DGUV Information „Feuerfest-, Turm- und Schornsteinbau“ Abschn. 4.7.6',
        ],
        beleg: 'MK',
    },

    // ═══ Hebezeuge und Foerdertechnik ═══════════════════════════════════════
    {
        id: 'standsicherheit_kran',
        name: 'Standsicherheitsnachweis Kranaufstellung und Kranfundament',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN', 'TWP'],
        wannBenoetigt: 'Für jeden Turmdrehkran am konkreten Aufstellort; Fundament bzw. Unterbau, Bodenpressung, Nähe zu Baugruben und Böschungen sowie Windlasten müssen nachgewiesen sein.',
        sigekoRelevanz: 'Vor dem Aufbau vorlegen lassen und mit Baugrundgutachten, Verbau- bzw. Böschungsnachweis und BE-Plan abgleichen; Kranstandort, Schwenkbereich und Überschneidungen mit Nachbarkranen, Leitungen und öffentlichem Raum im SiGe-Plan festlegen.',
        rechtsgrundlage: ['DGUV V 52 § 40 (1)', 'DGUV V 38 § 5 (1)'],
        beleg: 'MK',
        merkmale: ['kran'],
        alias: ['Kranfundamentstatik', 'Standsicherheitsnachweis Kran'],
    },
    {
        id: 'kranpruefbuch',
        name: 'Kranprüfbuch mit Prüfung nach Aufstellung',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Für jeden Kran vor der ersten Inbetriebnahme am Aufstellort und danach wiederkehrend; geprüft wird durch eine befähigte Person bzw. einen Prüfsachverständigen, die Ergebnisse stehen im Prüfbuch.',
        sigekoRelevanz: 'Vor Aufnahme des Kranbetriebs die Prüfung am Aufstellort nachweisen lassen und Kranführer, Anschläger und Verständigung (Funk, Einweiser) im SiGe-Plan benennen.',
        rechtsgrundlage: ['BetrSichV § 14 (4) und Anh. 3'],
        beleg: 'MK',
        merkmale: ['kran'],
        alias: ['Kranprüfbuch', 'Prüfbuch Kran'],
    },
    {
        id: 'rettungskonzept_kranfuehrer',
        name: 'Rettungskonzept Kranführer',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Kranen mit Führerhaus, vor allem Turmdrehkranen; für einen medizinischen Notfall in der Kabine oder auf dem Ausleger muss die Rettung aus der Höhe geplant sein.',
        sigekoRelevanz: 'Vor Inbetriebnahme vorlegen lassen, mit Feuerwehr bzw. Höhenrettung und den Erste-Hilfe-Regelungen im SiGe-Plan abstimmen und Rettungsgerät sowie unterwiesene Retter auf der Baustelle sicherstellen.',
        rechtsgrundlage: ['ArbSchG § 10', 'DGUV V 1 § 25'],
        beleg: 'praxis',
    },
    {
        id: 'kranueberschneidung',
        name: 'Kranüberschneidungsplan',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn sich die Arbeitsbereiche mehrerer Krane überschneiden, auch mit Kranen von Nachbarbaustellen; Arbeitsablauf und Verständigung der Kranführer sind festzulegen.',
        sigekoRelevanz: 'Kernaufgabe der Koordination: Schwenkbereiche, Höhenstaffelung, Arbeitsbereichsbegrenzung und Vorrangregeln mit allen Kranbetreibern abstimmen, in BE-Plan und SiGe-Plan eintragen und bei jedem weiteren Kran fortschreiben.',
        rechtsgrundlage: ['DGUV V 52 § 33 (1)'],
        beleg: 'MK',
        alias: ['Kranüberschneidung', 'Kranabstimmung'],
    },
    {
        id: 'abstuetznachweis',
        name: 'Abstützplan mit Nachweis der Bodenpressung',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Mobil-, Raupen- und Ladekrane, Betonpumpen, Hubarbeitsbühnen und Bohrgeräte, die abgestützt werden, besonders auf aufgefülltem, weichem oder unbekanntem Untergrund und neben Baugruben.',
        sigekoRelevanz: 'Vor dem Einsatz vorlegen lassen, Aufstellfläche, Unterlegplatten und Abstand zu Böschungen, Kanälen und Leitungen im BE-Plan sichern und den Einsatztag mit anderen Gewerken abstimmen (Sperrung, Gefahrbereich).',
        rechtsgrundlage: ['DGUV V 52 § 40 (2)'],
        beleg: 'MK',
        alias: ['Abstützplan', 'Bodenpressungsnachweis'],
    },
    {
        id: 'statik_personenaufnahmemittel',
        name: 'Statische Berechnung des Personenaufnahmemittels',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn Personen mit Arbeitskörben am Kran oder anderen hochziehbaren Personenaufnahmemitteln befördert werden, für das Personenaufnahmemittel und seine Einrichtung.',
        sigekoRelevanz: 'Vor dem ersten Einsatz vorlegen lassen; Personenbeförderung mit dem Kran nur zulassen, wenn Hubarbeitsbühne oder Gerüst nicht geeignet sind, und den Kranbetrieb für andere Gewerke während dieser Zeit regeln.',
        rechtsgrundlage: ['DGUV R 101-005 Abschn. 4.1.2.1'],
        beleg: 'MK',
    },
    {
        id: 'anzeige_personenaufnahmemittel',
        name: 'Anzeige an den UVT (Personenaufnahmemittel)',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor der ersten Inbetriebnahme eines Personenaufnahmemittels am Kran; die Personenbeförderung und die Sicherheitsmaßnahmen sind dem UVT mindestens zwei Wochen vorher schriftlich mitzuteilen.',
        sigekoRelevanz: 'Frist in den Bauablauf übernehmen und Kopie der Anzeige vor dem Einsatz vorlegen lassen.',
        vorlauf: '2 Wochen vor dem ersten Einsatz an den UVT',
        rechtsgrundlage: ['DGUV V 52 § 36 (4)', 'DGUV R 101-005 Abschn. 5.1.1'],
        beleg: 'MK',
    },
    {
        id: 'statik_bauaufzug',
        name: 'Statischer Nachweis der Verankerung (Bauaufzug, Transportbühne)',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn Bauaufzüge oder Transportbühnen anders als in der Betriebsanleitung vorgesehen am Bauwerk verankert werden (Abstände, Verankerungsgrund, Ankerkräfte).',
        sigekoRelevanz: 'Vor dem Aufbau vorlegen lassen, Verankerungspunkte mit Rohbau bzw. Fassadenbau abstimmen und die Zugänge an den Haltestellen gegen Absturz sichern lassen.',
        rechtsgrundlage: ['DGUV R 100-500 Kap. 2.30 Abschn. 3.20'],
        beleg: 'MK',
    },
    {
        id: 'nachweis_silo',
        name: 'Nachweis der standsicheren Aufstellung des Baustellensilos',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Mörtel-, Putz- und Bindemittelsilos; Aufstandsfläche, Bodenpressung und Windlast müssen den Herstellerangaben entsprechen.',
        sigekoRelevanz: 'Vor der Aufstellung vorlegen lassen und den Standort im BE-Plan festlegen: Abstand zu Baugruben und Verkehrswegen, Anfahrt für Silofahrzeuge, Schutz gegen Anfahren.',
        rechtsgrundlage: [DGUV_R_SPEZIALTIEFBAU + ' Abschn. 4.7.2'],
        beleg: 'MK',
    },
    {
        id: 'luftrechtliche_genehmigung',
        name: 'Luftrechtliche Genehmigung für Krane',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Wenn Krane im Bauschutzbereich eines Flughafens oder Landeplatzes, in Anflugschneisen oder über der dort zulässigen Höhe aufgestellt werden; die Genehmigung enthält oft Auflagen zu Höhe und Hinderniskennzeichnung.',
        sigekoRelevanz: 'Rechtzeitig beantragen lassen, weil die Bearbeitung Wochen dauern kann, und Höhenbegrenzung sowie Kennzeichnung in BE-Plan und Kranüberschneidungsplan übernehmen.',
        rechtsgrundlage: ['LuftVG §§ 12–17'],
        beleg: 'praxis',
    },
    {
        id: 'betriebsanleitung_hebezeugeinsatz',
        name: 'Betriebsanleitung mit Traglasttabelle für den Hebezeugeinsatz',
        art: 'B',
        gruppe: 'hebezeug',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn Bagger, Lader oder Spezialtiefbaumaschinen Lasten heben (Rohre, Verbauplatten, Bauteile); der Hersteller muss den Hebezeugeinsatz vorsehen, Traglasttabelle, Lastmomentüberwachung und Sicherungen müssen vorhanden sein.',
        sigekoRelevanz: 'Vor dem Einsatz Herstellerfreigabe und Traglasttabelle vorlegen lassen und Anschläger, Verständigung und Gefahrbereich beim Heben wie im Kranbetrieb regeln.',
        rechtsgrundlage: ['BetrSichV § 9 (1)', 'DGUV R 100-500 Kap. 2.12 Abschn. 3.16'],
        beleg: 'MK',
        alias: ['Traglasttabelle', 'Herstellerfreigabe Hebezeugeinsatz'],
    },

    // ═══ Abbruch, Schadstoffe, Staeube ══════════════════════════════════════
    {
        id: 'schadstoffgutachten',
        name: 'Schadstoffgutachten',
        art: 'A',
        gruppe: 'schadstoff',
        lieferant: ['GUT', 'BH'],
        wannBenoetigt: 'Vor Abbruch-, Rückbau-, Umbau- und Sanierungsarbeiten im Bestand, vor allem bei Baujahr vor 1993; Asbest, künstliche Mineralfasern, PCB, PAK, Holzschutzmittel, Schwermetalle und biologische Belastungen müssen vor Beginn erkundet sein.',
        sigekoRelevanz: 'Beim Bauherrn anfordern und prüfen, ob alle betroffenen Bauteile beprobt und bewertet sind; Fundstellen in SiGe-Plan und BE-Plan (Schwarzbereiche) übernehmen, die Schadstoffsanierung vor die übrigen Arbeiten legen und Restbelastungen für spätere Arbeiten dokumentieren.',
        rechtsgrundlage: ['GefStoffV § 6', 'DGUV R 101-004 Abschn. 11.1', 'TRGS 519'],
        beleg: 'MK',
        ufsa: true,
        asplan: 'grundlage',
        merkmale: ['abbruch', 'bestand', 'gefahrstoffeAnhang2'],
        alias: ['Schadstoffkataster', 'Schadstofferkundung', 'Gebäudeschadstoffgutachten'],
    },
    {
        id: 'analysenberichte',
        name: 'Analysenberichte der Schadstoffuntersuchung',
        art: 'A',
        gruppe: 'schadstoff',
        lieferant: ['GUT'],
        wannBenoetigt: 'Als Anlage zum Erkundungs- bzw. Schadstoffgutachten; die Laborergebnisse je Probe, aus denen sich Konzentrationen und Schutzstufen ableiten.',
        sigekoRelevanz: 'Mit dem Gutachten anfordern und als Anlage zum A+S-Plan führen; bei Abweichungen zwischen Befund und Bewertung beim Gutachter nachfragen.',
        rechtsgrundlage: ['DGUV R 101-004'],
        beleg: 'praxis',
        asplan: 'anlage',
        alias: ['Laborberichte', 'Prüfberichte Labor'],
    },
    {
        id: 'messkonzept',
        name: 'Messkonzept mit Freigabemessung',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN', 'GUT'],
        wannBenoetigt: 'Bei Arbeiten in kontaminierten Bereichen und bei Schadstoffsanierungen; Luftmessungen während der Arbeit und die Freigabemessung vor der Wiedernutzung der Bereiche sind festzulegen.',
        sigekoRelevanz: 'Vorlegen lassen und festlegen, dass sanierte Bereiche erst nach dokumentierter Freigabemessung für andere Gewerke freigegeben werden; Messergebnisse als Anlage zum A+S-Plan führen.',
        rechtsgrundlage: ['DGUV R 101-004', 'TRGS 519'],
        beleg: 'praxis',
        asplan: 'anlage',
        alias: ['Freigabemessung', 'Messkonzept'],
    },
    {
        id: 'abbruchanweisung',
        name: 'Abbruchanweisung',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Abbruch- und Rückbauarbeiten mit besonderen sicherheitstechnischen Anforderungen; Verfahren, Reihenfolge, Standsicherheit der Restkonstruktion, Gefahrbereiche und Schadstoffbefunde werden schriftlich festgelegt.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob Schadstoffgutachten, Leitungsfreischaltung und Standsicherheit der Zwischenzustände berücksichtigt sind; Gefahrbereiche, Abwurfstellen und Sperrzeiten mit den übrigen Gewerken koordinieren.',
        rechtsgrundlage: ['DGUV V 38 § 4', 'DGUV R 101-004 Abschn. 11.8.1'],
        beleg: 'MK',
        asplan: 'anlage',
        merkmale: ['abbruch'],
        alias: ['Rückbauanweisung'],
    },
    {
        id: 'standsicherheit_abbruch',
        name: 'Standsicherheitsnachweis für Abbruchzustände',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN', 'TWP'],
        wannBenoetigt: 'Wenn tragende Bauteile abgebrochen oder geschwächt werden und die Standsicherheit der verbleibenden Konstruktion in den Zwischenzuständen nicht offensichtlich gegeben ist.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, erforderliche Abstützungen und die festgelegte Abbruchreihenfolge in Abbruchanweisung und SiGe-Plan übernehmen und den Aufenthalt anderer Gewerke in einsturzgefährdeten Bereichen ausschließen.',
        rechtsgrundlage: ['DGUV V 38 § 4', 'DGUV V 38 § 5 (1)', 'DGUV R 101-038 Abschn. 4'],
        beleg: 'MK',
        merkmale: ['abbruch'],
        alias: ['Abbruchstatik'],
    },
    {
        id: 'sachkunde_asbest',
        name: 'Sachkundenachweis nach TRGS 519',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Abbruch-, Sanierungs- und Instandhaltungsarbeiten an asbesthaltigen Materialien; Aufsichtführender und verantwortliche Person brauchen eine gültige Sachkunde.',
        sigekoRelevanz: 'Gültigkeit vor Beginn prüfen (Fortbildungsfrist) und den Aufsichtführenden mit Namen im SiGe-Plan benennen.',
        rechtsgrundlage: ['TRGS 519 Nrn. 2.7 und 3.1'],
        beleg: 'MK',
        asplan: 'anlage',
        alias: ['Asbest-Sachkunde', 'Sachkunde TRGS 519'],
    },
    {
        id: 'anzeige_asbest',
        name: 'Anzeige der Asbestarbeiten an die Behörde',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Beginn von Abbruch-, Sanierungs- und Instandhaltungsarbeiten mit Asbest an die zuständige Behörde, mit Durchschrift an den Unfallversicherungsträger.',
        sigekoRelevanz: 'Kopie der Anzeige mit Eingangsdatum vor Beginn vorlegen lassen und mit dem Bauablauf abgleichen.',
        rechtsgrundlage: ['TRGS 519 Nr. 3.2 (1)'],
        beleg: 'MK',
        asplan: 'anlage',
        alias: ['Asbestanzeige'],
    },
    {
        id: 'arbeitsplan_trgs519',
        name: 'Arbeitsplan nach TRGS 519',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Abbruch- und Sanierungsarbeiten an Asbest und vor dem Entfernen asbesthaltiger Abfälle, auf Grundlage der Gefährdungsbeurteilung mit Verfahren, Schutzmaßnahmen, Abschottung und Entsorgung.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, Abschottung, Schleusen, Unterdruckhaltung und Transportwege in BE-Plan und SiGe-Plan übernehmen und den Zutritt anderer Gewerke bis zur Freigabemessung ausschließen.',
        rechtsgrundlage: ['TRGS 519 Nr. 4.2'],
        beleg: 'MK',
        asplan: 'anlage',
        alias: ['Arbeitsplan Asbest'],
    },
    {
        id: 'betriebsanweisung_gefahrstoffe',
        name: 'Betriebsanweisung Gefahrstoffe',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Tätigkeiten mit Gefahrstoffen wie lösemittelhaltigen Lacken, Klebern und Beschichtungen, Epoxid- und Kunstharzen sowie bei Asbest und in kontaminierten Bereichen; arbeitsbereichs- und stoffbezogen in verständlicher Sprache.',
        sigekoRelevanz: 'Stichprobenartig vorlegen lassen und daraus die Auswirkungen auf andere Gewerke ableiten (Lüftung, Zündquellenverbot, Zutrittsbeschränkung, Arbeitszeiten), die im SiGe-Plan koordiniert werden.',
        rechtsgrundlage: ['GefStoffV § 14 (1)', 'TRGS 555', 'TRGS 519 Nr. 11', 'DGUV R 101-004 Abschn. 16'],
        beleg: 'MK',
        asplan: 'anlage',
        alias: ['Betriebsanweisung nach § 14 GefStoffV'],
    },
    {
        id: 'arbeitsplan_trgs521',
        name: 'Arbeitsplan nach TRGS 521 (alte Mineralwolle)',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Ausbau, Rückbau oder Sanierung alter Mineralwolle-Dämmstoffe (in der Regel vor 2000 eingebaut und ohne Nachweis der Freizeichnung); Verfahren, Staubminderung, Verpackung und Schutzausrüstung werden vorab festgelegt.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und Abschottung, Absaugung und staubdichte Entsorgung mit den übrigen Gewerken im Gebäude abstimmen, damit keine Faserstäube in Nachbarbereiche gelangen.',
        rechtsgrundlage: ['TRGS 521'],
        beleg: 'praxis',
        asplan: 'anlage',
        alias: ['Arbeitsplan TRGS 521'],
    },
    {
        id: 'pak_untersuchung',
        name: 'PAK-Untersuchung des Straßenaufbruchs',
        art: 'A',
        gruppe: 'schadstoff',
        lieferant: ['BH', 'GUT'],
        wannBenoetigt: 'Vor dem Aufbrechen oder Fräsen alter Asphalt- und Straßenbefestigungen, die teer- bzw. pechhaltige Schichten enthalten können.',
        sigekoRelevanz: 'Beim Bauherrn anfordern; bei teerhaltigem Material staub- und dampfarme Verfahren und Schutzmaßnahmen festlegen lassen und den Arbeits- und Sicherheitsplan nach TRGS 524 auslösen.',
        rechtsgrundlage: ['TRGS 551', 'TRGS 524'],
        beleg: 'praxis',
        asplan: 'grundlage',
        alias: ['PAK-Untersuchung', 'Teeruntersuchung'],
    },
    {
        id: 'gb_trgs559',
        name: 'Gefährdungsbeurteilung Quarzstaub (TRGS 559)',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Stemm-, Fräs-, Schleif-, Bohr- und Trennarbeiten an Beton, Mauerwerk, Estrich, Naturstein oder Asphalt; staubarme Verfahren, Absaugung und Atemschutz sind festzulegen.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob staubarme Verfahren mit Absaugung vorgesehen sind; staubintensive Arbeiten räumlich oder zeitlich von anderen Gewerken trennen und Staubschutzwände im SiGe-Plan festlegen.',
        rechtsgrundlage: ['GefStoffV § 6', 'TRGS 559 Nr. 3'],
        beleg: 'MK',
        alias: ['GB TRGS 559', 'Gefährdungsbeurteilung TRGS 559'],
    },
    {
        id: 'gb_trgs553',
        name: 'Gefährdungsbeurteilung Holzstaub (TRGS 553)',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Holzbearbeitung auf der Baustelle (Sägen, Fräsen, Schleifen, Parkettschleifen), besonders bei Hartholz, dessen Staub als krebserzeugend eingestuft ist.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, Absaugung und Reinigung mit Entstaubern statt Fegen festlegen und Holzbearbeitungsplätze im BE-Plan abseits anderer Arbeitsplätze anordnen.',
        rechtsgrundlage: ['GefStoffV § 6', 'TRGS 553'],
        beleg: 'MK',
        alias: ['GB TRGS 553', 'Gefährdungsbeurteilung TRGS 553'],
    },
    {
        id: 'sdb',
        name: 'Sicherheitsdatenblätter',
        kurz: 'SDB',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Für alle eingesetzten Gefahrstoffe, etwa Lacke, Kleber, Beschichtungen, Lösemittel, Bauchemie und Kraftstoffe; der Hersteller liefert sie, vor Beginn müssen sie vorliegen.',
        sigekoRelevanz: 'Vor Beginn anfordern und auf Wirkungen prüfen, die andere Gewerke betreffen (Dämpfe, Entzündbarkeit, Ex-Bereiche, Lüftungsbedarf); daraus Arbeitszeiten, Lüftung und Zutrittsbeschränkungen im SiGe-Plan ableiten.',
        rechtsgrundlage: ['GefStoffV § 6', 'REACH-Verordnung Art. 31'],
        beleg: 'praxis',
        alias: ['Sicherheitsdatenblatt'],
    },
    {
        id: 'gutachten_biostoffe',
        name: 'Gutachten zur Belastung mit Biostoffen',
        art: 'A',
        gruppe: 'schadstoff',
        lieferant: ['GUT', 'BH'],
        wannBenoetigt: 'Vor Arbeiten in Bereichen mit Schimmelbefall, Taubenkot, Fäkalien oder anderen biologischen Belastungen; Art und Umfang bestimmen Schutzstufe und Sanierungsverfahren.',
        sigekoRelevanz: 'Beim Bauherrn anfordern, belastete Bereiche abgrenzen, die Sanierung vor die übrigen Arbeiten legen und den Zutritt bis zur Freigabe regeln.',
        rechtsgrundlage: ['BioStoffV § 4', 'DGUV I 201-028'],
        beleg: 'praxis',
        asplan: 'grundlage',
        alias: ['Schimmelgutachten'],
    },
    {
        id: 'betriebsanweisung_biostoffe',
        name: 'Betriebsanweisung Biostoffe',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Aufnahme von Tätigkeiten mit Biostoffen (Schimmel, Taubenkot, Abwasser, Fäkalien), arbeitsbereichs- und biostoffbezogen.',
        sigekoRelevanz: 'Vorlegen lassen und die Hygienemaßnahmen (Waschgelegenheit, Schwarz-Weiß-Trennung, Reinigung) mit der Baustelleneinrichtung abstimmen.',
        rechtsgrundlage: ['BioStoffV § 14 (1)'],
        beleg: 'MK',
        asplan: 'anlage',
        alias: ['Betriebsanweisung nach § 14 BioStoffV'],
    },
    {
        id: 'nachweis_trgs554',
        name: 'Nachweis zu Dieselmotoremissionen (TRGS 554)',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn dieselbetriebene Geräte oder Fahrzeuge in Hallen, Innenräumen, Tiefgaragen, Tunneln oder anderen schlecht belüfteten Bereichen eingesetzt werden; nachzuweisen ist, dass die Arbeitsplatzgrenzwerte eingehalten werden.',
        sigekoRelevanz: 'Vor dem Einsatz vorlegen lassen (Partikelfilter, Lüftung, Messungen), bevorzugt elektrische Geräte fordern und die Einsatzzeiten mit anderen Gewerken im selben Raum abstimmen.',
        rechtsgrundlage: ['TRGS 554 Nr. 4.4'],
        beleg: 'MK',
        alias: ['Nachweis TRGS 554'],
    },
    {
        id: 'lueftungskonzept',
        name: 'Lüftungskonzept',
        art: 'B',
        gruppe: 'schadstoff',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn in geschlossenen Räumen Verbrennungsmotoren laufen oder Gase, Dämpfe und Stäube entstehen, die nicht allein durch natürliche Lüftung abgeführt werden.',
        sigekoRelevanz: 'Vorlegen lassen und prüfen, dass Zu- und Abluft keine anderen Arbeitsbereiche belasten; die Lüftungsanlage als gemeinsam genutzte Einrichtung im SiGe-Plan führen.',
        rechtsgrundlage: ['TRGS 554', 'ASR A3.6'],
        beleg: 'praxis',
    },

    // ═══ Brand, Energie, Sonderverfahren ════════════════════════════════════
    {
        id: 'schweisserlaubnis',
        name: 'Schweißerlaubnis (Erlaubnisschein für feuergefährliche Arbeiten)',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN', 'BET'],
        wannBenoetigt: 'Für Schweiß-, Schneid-, Löt-, Trennschleif- und andere Heißarbeiten in brand- oder explosionsgefährdeten Bereichen, im Bestand und überall dort, wo brennbare Stoffe nicht vollständig entfernt werden können.',
        sigekoRelevanz: 'Verfahren im SiGe-Plan festlegen (wer stellt aus, wer prüft den Bereich, Brandwache und Nachkontrolle), im Bestand mit dem Erlaubnisscheinverfahren des Betreibers verknüpfen und Heißarbeiten zeitlich von Arbeiten mit brennbaren Stoffen trennen.',
        rechtsgrundlage: ['BetrSichV § 12 (2)', 'DGUV R 100-500 Kap. 2.26 Abschn. 3.8.2'],
        beleg: 'MK',
        alias: ['Schweißerlaubnis', 'Erlaubnisschein feuergefährliche Arbeiten', 'Heißarbeitserlaubnis'],
    },
    {
        id: 'bma_abschaltung',
        name: 'Abschaltprotokoll Brandmeldeanlage',
        art: 'A',
        gruppe: 'brand_energie',
        lieferant: ['BET'],
        wannBenoetigt: 'Wenn im Bestand mit Brandmeldeanlage Staub, Rauch oder Hitze entstehen (Heißarbeiten, Trennschleifen, Stemmen) und Melder im Arbeitsbereich abgeschaltet oder abgedeckt werden müssen.',
        sigekoRelevanz: 'Mit dem Betreiber Abschaltbereich, Dauer und Ersatzmaßnahmen (Brandwache, Rückschaltung bei Arbeitsende) festlegen, im SiGe-Plan dokumentieren und die Abschaltung immer an einen Erlaubnisschein koppeln.',
        beleg: 'praxis',
        alias: ['BMA-Abschaltung'],
    },
    {
        id: 'betriebsanweisung_fluessiggas',
        name: 'Betriebsanweisung Flüssiggasanlage',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Für jede Anlage zur Verwendung von Flüssiggas auf der Baustelle (Gasbrenner, Bitumenschmelzgeräte, Heizgeräte, Flaschenbündel); sie muss am Betriebsort vorliegen.',
        sigekoRelevanz: 'Vorlegen lassen und Aufstellung und Lagerung der Gasflaschen (nicht unter Erdgleiche, nicht in Treppenräumen und Fluchtwegen) im BE-Plan festlegen; Zündquellen anderer Gewerke berücksichtigen.',
        rechtsgrundlage: ['DGUV V 79 § 5'],
        beleg: 'MK',
    },
    {
        id: 'brandschutzordnung',
        name: 'Brandschutzordnung für die Baustelle',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN', 'BH'],
        wannBenoetigt: 'Auf Baustellen mit erhöhter Brandgefahr, im Bestand, bei laufendem Betrieb und auf größeren Baustellen; sie regelt Brandverhütung, Verhalten im Brandfall, Alarmierung und Löschmittel.',
        sigekoRelevanz: 'Erstellung zuweisen bzw. abstimmen, mit Alarmplan, Flucht- und Rettungsplan und der Brandschutzordnung des Betreibers verzahnen und in die Baustelleneinweisung übernehmen.',
        rechtsgrundlage: ['ASR A2.2', 'DIN 14096'],
        beleg: 'praxis',
        alias: ['Baustellen-Brandschutzordnung'],
    },
    {
        id: 'brandschutzkonzept',
        name: 'Brandschutzkonzept des Gebäudes',
        art: 'A',
        gruppe: 'brand_energie',
        lieferant: ['FP', 'OP'],
        wannBenoetigt: 'Bei Arbeiten im Bestand und bei laufendem Betrieb; es zeigt Brandabschnitte, Rettungswege und brandschutztechnische Anlagen, die während der Bauzeit wirksam bleiben oder ersetzt werden müssen.',
        sigekoRelevanz: 'Beim Bauherrn anfordern, Eingriffe in Brandabschnitte, Rettungswege und Brandmeldeanlage je Bauphase mit dem Brandschutzplaner abstimmen, Ersatzmaßnahmen im SiGe-Plan festlegen und das Konzept in die Unterlage für spätere Arbeiten übernehmen.',
        beleg: 'praxis',
        ufsa: true,
        merkmale: ['betriebLaeuftWeiter'],
    },
    {
        id: 'flucht_rettungsplan',
        name: 'Flucht- und Rettungsplan',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN', 'BH'],
        wannBenoetigt: 'Wenn Lage, Ausdehnung und Art der Baustelle es erfordern, etwa bei mehrgeschossigen Gebäuden, unübersichtlichen Rettungswegen oder im Bestand; er wird ausgehängt und mit dem Baufortschritt angepasst.',
        sigekoRelevanz: 'Erstellung zuweisen bzw. abstimmen, den Aushang an geeigneten Stellen kontrollieren, bei jeder Änderung von Treppen, Zugängen und Gerüsten fortschreiben lassen und in die Baustelleneinweisung übernehmen.',
        rechtsgrundlage: ['ArbStättV § 4 (4)', 'ASR A2.3'],
        beleg: 'MK',
        alias: ['Flucht- und Rettungswegeplan', 'Fluchtwegeplan'],
    },
    {
        id: 'alarmplan',
        name: 'Alarmplan',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN', 'BH'],
        wannBenoetigt: 'Für jede Baustelle mit mehreren Gewerken: Notrufnummern, Meldewege, Sammelplatz, Ersthelfer und Anfahrt der Rettungskräfte.',
        sigekoRelevanz: 'Mit den Angaben aller Gewerke abstimmen, aushängen lassen und in die Baustelleneinweisung übernehmen; Anfahrt und Übergabepunkt für Rettungsdienst und Feuerwehr im BE-Plan freihalten.',
        rechtsgrundlage: ['ArbSchG § 10', 'DGUV V 1 § 22'],
        beleg: 'praxis',
        asplan: 'anlage',
        merkmale: ['mehrereArbeitgeber'],
        alias: ['Notfallplan', 'Notrufplan'],
    },
    {
        id: 'freischaltprotokoll',
        name: 'Freischaltprotokoll (fünf Sicherheitsregeln)',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Arbeiten an elektrischen Anlagen; Freischalten, Sichern gegen Wiedereinschalten, Feststellen der Spannungsfreiheit, Erden und Kurzschließen sowie Abdecken benachbarter Teile werden dokumentiert.',
        sigekoRelevanz: 'Arbeiten mehrere Gewerke an derselben Anlage, festlegen lassen, wer freischaltet und wieder zuschaltet; Schaltzustand und Sperrschilder in der Einweisung erklären.',
        rechtsgrundlage: ['DGUV V 3 § 6', 'DIN VDE 0105-100'],
        beleg: 'praxis',
    },
    {
        id: 'schaltberechtigung',
        name: 'Benennung des Anlagenverantwortlichen und Schaltberechtigung',
        art: 'A',
        gruppe: 'brand_energie',
        lieferant: ['BET'],
        wannBenoetigt: 'Bei Arbeiten an bestehenden elektrischen Anlagen eines Betreibers; nur benannte, berechtigte Personen dürfen schalten und Arbeiten freigeben.',
        sigekoRelevanz: 'Beim Betreiber erfragen, mit Erreichbarkeit im SiGe-Plan benennen und Schalthandlungen durch Baufirmen nur mit Freigabe des Anlagenverantwortlichen zulassen.',
        rechtsgrundlage: ['DIN VDE 0105-100'],
        beleg: 'praxis',
        alias: ['Schaltberechtigung', 'Anlagenverantwortlicher'],
    },
    {
        id: 'arbeitsanweisung_aus',
        name: 'Arbeitsanweisung für Arbeiten unter Spannung',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn ausnahmsweise an unter Spannung stehenden Teilen gearbeitet werden muss, weil Freischalten nicht möglich ist; nur mit besonderer Ausbildung, Werkzeug und schriftlicher Anweisung.',
        sigekoRelevanz: 'Begründung und Arbeitsanweisung vor Beginn vorlegen lassen, den Arbeitsbereich absperren und andere Gewerke fernhalten.',
        rechtsgrundlage: ['DGUV V 3 § 8', 'DGUV R 103-011'],
        beleg: 'praxis',
        alias: ['Arbeitsanweisung AuS'],
    },
    {
        id: 'pruefprotokoll_baustromverteiler',
        name: 'Prüfprotokoll Baustromverteiler',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor der ersten Inbetriebnahme der Baustromversorgung und danach wiederkehrend; Anschlusspunkt, Baustromverteiler und Fehlerstrom-Schutzeinrichtungen werden von einer Elektrofachkraft geprüft.',
        sigekoRelevanz: 'Prüfprotokoll vor der Nutzung durch andere Gewerke vorlegen lassen und im SiGe-Plan festlegen, wer die gemeinsam genutzten Verteiler betreibt, prüft und erweitert.',
        rechtsgrundlage: ['DGUV V 3 § 5', 'DGUV I 203-006'],
        beleg: 'praxis',
        alias: ['Prüfprotokoll Baustrom'],
    },
    {
        id: 'betriebsanweisung_stromerzeuger',
        name: 'Betriebsanweisung Stromerzeuger',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn die Baustelle oder einzelne Arbeitsplätze über Stromerzeuger (Aggregate) versorgt werden; die Schutzmaßnahme hängt von Bauart und Anschlussart ab.',
        sigekoRelevanz: 'Vorlegen lassen und prüfen, ob die Schutzmaßnahme (Schutztrennung bzw. Fehlerstrom-Schutzeinrichtung, Potentialausgleich) zu den Geräten anderer Gewerke passt; Aufstellort wegen Abgasen und Lärm im BE-Plan festlegen.',
        rechtsgrundlage: ['DGUV I 203-032 Abschn. 3'],
        beleg: 'MK',
    },
    {
        id: 'pruefnachweis_tankanlage',
        name: 'Prüfnachweis der Tankanlage',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Dieseltankanlagen der Baustelleneinrichtung; Eignung und Prüfung des Behälters, Abfüllsicherung und Aufstellung sind vor der Inbetriebnahme nachzuweisen.',
        sigekoRelevanz: 'Vor der Aufstellung vorlegen lassen und den Standort mit Anfahrschutz, Abstand zu Zündquellen und Baugruben sowie Löschmitteln im BE-Plan festlegen.',
        rechtsgrundlage: ['BetrSichV § 14', 'AwSV'],
        beleg: 'praxis',
    },
    {
        id: 'sprengerlaubnis',
        name: 'Sprengerlaubnis und Befähigungsschein (SprengG)',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Vor jeder Sprengarbeit; das Unternehmen braucht eine Erlaubnis, der verantwortliche Sprengberechtigte einen Befähigungsschein nach dem Sprengstoffgesetz.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und Sprengzeiten, Sperrbereiche und Warnsignale mit allen Gewerken und Anliegern koordinieren.',
        rechtsgrundlage: ['SprengG §§ 7, 20'],
        beleg: 'praxis',
        merkmale: ['sprengarbeiten'],
    },
    {
        id: 'sprengplan',
        name: 'Sprengplan',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Für jede Sprengung: Bohr- und Ladeschema, Sprengstoffmengen, Sperrbereich, Sicherungsposten und Ablauf.',
        sigekoRelevanz: 'Vor der Sprengung vorlegen lassen, Sperrbereich und Räumung der Baustelle im SiGe-Plan festlegen und die Rückkehr erst nach Freigabe durch den Sprengberechtigten zulassen.',
        beleg: 'praxis',
        merkmale: ['sprengarbeiten'],
    },
    {
        id: 'anzeige_druckluft',
        name: 'Anzeige an die Behörde (Arbeiten in Druckluft)',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Arbeiten in Druckluft (Senkkasten, Vortrieb unter Überdruck), spätestens zwei Wochen vor Beginn.',
        sigekoRelevanz: 'Frist in den Bauablauf übernehmen, Kopie der Anzeige vorlegen lassen und Schleusenbetrieb, arbeitsmedizinische Überwachung und Notfallorganisation mit dem SiGe-Plan abstimmen.',
        vorlauf: '2 Wochen vor Beginn an die zuständige Behörde',
        rechtsgrundlage: ['DruckLV § 3 (1)'],
        beleg: 'MK',
        merkmale: ['druckluft'],
    },
    {
        id: 'laserschutzbeauftragter',
        name: 'Bestellung des Laserschutzbeauftragten',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor dem Betrieb von Lasereinrichtungen der Klassen 3R, 3B und 4; schriftlich und mit Nachweis der Sachkunde.',
        sigekoRelevanz: 'Vorlegen lassen, Laserbereich und Strahlführung im SiGe-Plan festlegen und andere Gewerke aus dem Strahlbereich fernhalten.',
        rechtsgrundlage: ['OStrV § 5 (2)'],
        beleg: 'MK',
    },
    {
        id: 'betriebsanweisung_laser',
        name: 'Betriebsanweisung Lasereinrichtung',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Lasereinrichtungen der Klassen 3R, 3B und 4, unter Berücksichtigung der Betriebsanleitung und der Einsatzbedingungen auf der Baustelle.',
        sigekoRelevanz: 'Vorlegen lassen und Kennzeichnung des Laserbereichs sowie die Zutrittsregelung mit den übrigen Gewerken abstimmen.',
        rechtsgrundlage: ['OStrV', 'TROS IOS Teil 3 Nr. 7'],
        beleg: 'MK',
    },
    {
        id: 'strahlenschutz',
        name: 'Genehmigung bzw. Anzeige nach Strahlenschutzrecht',
        art: 'B',
        gruppe: 'brand_energie',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Bei Durchstrahlungsprüfungen, Isotopensonden zur Dichte- und Feuchtemessung und anderem Umgang mit radioaktiven Stoffen oder Röntgenstrahlung auf der Baustelle.',
        sigekoRelevanz: 'Vor dem Einsatz Genehmigung bzw. Anzeige und den Strahlenschutzbeauftragten vorlegen lassen und Kontroll- und Sperrbereiche sowie Prüfzeiten (oft nachts) mit allen Gewerken koordinieren.',
        rechtsgrundlage: ['StrlSchG', 'StrlSchV'],
        beleg: 'praxis',
        merkmale: ['strahlung'],
    },

    // ═══ Verkehr, Wasser, Bahn ══════════════════════════════════════════════
    {
        id: 'vrao',
        name: 'Verkehrsrechtliche Anordnung mit Verkehrszeichenplan',
        kurz: 'VRAO',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Für alle Arbeitsstellen im oder am öffentlichen Verkehrsraum (Fahrbahn, Gehweg, Radweg), auch für Kranaufstellung, Anlieferung und Gerüste im Straßenraum.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen und prüfen, ob die Absicherung vor Ort dem Verkehrszeichenplan entspricht; Sicherheitsabstände zum Verkehr, Fußgängerführung und Baustellenzufahrten in SiGe-Plan und BE-Plan übernehmen.',
        rechtsgrundlage: ['StVO § 45 (6)', 'RSA 21'],
        beleg: 'MK',
        merkmale: ['strassenbaustelle'],
        alias: ['Verkehrsrechtliche Anordnung', 'Verkehrszeichenplan'],
    },
    {
        id: 'sondernutzungserlaubnis',
        name: 'Sondernutzungserlaubnis',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Wenn öffentliche Straßen, Gehwege oder Plätze für Gerüste, Container, Lagerflächen, Kranaufstellung oder Baustellenzufahrten in Anspruch genommen werden.',
        sigekoRelevanz: 'Vorliegen vor Beginn prüfen und die genehmigten Flächen und Zeiten in den BE-Plan übernehmen, damit Lager- und Gerätestandorte nicht auf ungesicherte Verkehrsflächen ausweichen.',
        rechtsgrundlage: ['FStrG § 8'],
        beleg: 'praxis',
        merkmale: ['strassenbaustelle'],
    },
    {
        id: 'betra',
        name: 'Betriebs- und Bauanweisung der Bahn',
        kurz: 'Betra',
        art: 'A',
        gruppe: 'verkehr_wasser',
        lieferant: ['BEH'],
        wannBenoetigt: 'Bei Arbeiten im oder am Gleisbereich, die den Bahnbetrieb berühren; Sperrpausen, Langsamfahrstellen und Sicherungsmaßnahmen ordnet der Infrastrukturbetreiber an.',
        sigekoRelevanz: 'Über den Bauherrn beim Infrastrukturbetreiber anfordern, Sperrzeiten und Sicherungsmaßnahmen in den Bauablauf übernehmen und Arbeiten anderer Gewerke im Gleisbereich nur innerhalb der Betra-Zeiten zulassen.',
        rechtsgrundlage: ['DB-Richtlinie 406'],
        beleg: 'praxis',
    },
    {
        id: 'sicherungsplan_gleis',
        name: 'Sicherungsplan für Arbeiten im Gleisbereich',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Bei Arbeiten im Gefahrbereich von Gleisen; Sicherungsmaßnahmen gegen Gefahren aus dem Bahnbetrieb (feste Absperrung, Sicherungsposten, Warnanlagen) werden vor Beginn festgelegt.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, prüfen, ob alle Gewerke mit Arbeitsplätzen oder Wegen im Gleisbereich erfasst sind, und Zugänge und Wege im BE-Plan außerhalb des Gefahrbereichs führen.',
        rechtsgrundlage: ['UVV „Arbeiten im Bereich von Gleisen“'],
        beleg: 'praxis',
    },
    {
        id: 'rettungskonzept_wasser',
        name: 'Rettungskonzept für Arbeiten am und über Wasser',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei allen Arbeiten mit Ertrinkungsgefahr: an Ufern, auf schwimmenden Geräten, über Gewässern und an Hochwasserschutzanlagen.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, Rettungsmittel (Rettungsboot, Rettungsringe, Rettungswesten), Bereitschaft und Alarmierung im SiGe-Plan festlegen und mit allen Gewerken am Wasser abstimmen.',
        rechtsgrundlage: ['DGUV V 38 § 8 (6)', 'DGUV R 101-038 Abschn. 8.6'],
        beleg: 'MK',
        merkmale: ['gewaesser'],
    },
    {
        id: 'genehmigung_wsa',
        name: 'Strom- und schifffahrtspolizeiliche Genehmigung',
        art: 'A',
        gruppe: 'verkehr_wasser',
        lieferant: ['BEH', 'BH'],
        wannBenoetigt: 'Bei Arbeiten an, in oder über Bundeswasserstraßen und in Häfen, die den Schiffsverkehr berühren; zuständig sind das Wasserstraßen- und Schifffahrtsamt bzw. die Hafenbehörde.',
        sigekoRelevanz: 'Über den Bauherrn anfordern und Auflagen (Sperrzeiten, Kennzeichnung schwimmender Geräte, Sicherungsfahrzeuge) in den SiGe-Plan übernehmen.',
        rechtsgrundlage: ['WaStrG § 31'],
        beleg: 'praxis',
        alias: ['Genehmigung WSA', 'Genehmigung Hafenbehörde'],
    },
    {
        id: 'hochwasser_raeumungsplan',
        name: 'Hochwasser- und Räumungsplan',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN'],
        wannBenoetigt: 'Bei Baustellen in Überschwemmungsgebieten, an Fließgewässern und an der Küste; Pegelgrenzen, Alarmstufen und die Räumung von Geräten, Material und Baugruben werden vorab festgelegt.',
        sigekoRelevanz: 'Vorlegen lassen und Pegelbeobachtung, Warnkette und Räumungsreihenfolge mit allen Gewerken abstimmen und im SiGe-Plan festhalten.',
        beleg: 'praxis',
        merkmale: ['gewaesser'],
        alias: ['Hochwasserplan', 'Räumungsplan'],
    },
    {
        id: 'tauchplan',
        name: 'Tauchplan',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Taucherarbeiten mit Tauchtiefen über 10 m oder besonderen Erschwernissen (Strömung, Sicht, enge Räume); der Tauchereinsatzleiter stellt ihn auf.',
        sigekoRelevanz: 'Vor Beginn vorlegen lassen, Tauchzeiten mit Schiffsverkehr, Pumpen, Schleusen und Arbeiten anderer Gewerke über dem Taucher abstimmen (keine Lasten, kein Ansaugen) und die Rettungskette prüfen.',
        rechtsgrundlage: ['DGUV V 40 § 16 (1)'],
        beleg: 'MK',
        merkmale: ['taucharbeiten'],
    },
    {
        id: 'stabilitaet_schwimmend',
        name: 'Stabilitätsberechnung des schwimmenden Geräts',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Pontons, Schuten, Barkassen und andere schwimmende Geräte, auf denen gearbeitet wird; der rechnerische Nachweis der Schwimmfähigkeit und Kentersicherheit ist von einem Sachverständigen zu prüfen und dem UVT zuzusenden.',
        sigekoRelevanz: 'Den geprüften Nachweis mit Versandnachweis an den UVT vor dem Einsatz vorlegen lassen und zulässige Beladung und Freibord als Grenzen im SiGe-Plan festhalten.',
        rechtsgrundlage: ['UVV „Schwimmende Geräte“ § 5 (1)'],
        beleg: 'MK',
        alias: ['Stabilitätsberechnung'],
    },
    {
        id: 'standsicherheit_geraet_schwimmend',
        name: 'Nachweis der Standsicherheit und Befestigung des Geräts auf dem Schwimmkörper',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN'],
        wannBenoetigt: 'Wenn Bagger, Krane, Bohr- oder Rammgeräte auf Pontons oder Schuten arbeiten; die zulässigen Neigungswinkel der Maschine müssen im Betrieb eingehalten werden.',
        sigekoRelevanz: 'Vor dem Einsatz vorlegen lassen, mit der Stabilitätsberechnung abgleichen und Lastfälle beim Heben über Bord sowie die Befestigung der Maschine an Deck prüfen lassen.',
        rechtsgrundlage: ['BetrSichV § 9', 'DGUV R 100-500 Kap. 2.12 Abschn. 3.5'],
        beleg: 'MK',
    },
    {
        id: 'schiffsattest',
        name: 'Zulassung bzw. Schiffsattest des schwimmenden Geräts',
        art: 'B',
        gruppe: 'verkehr_wasser',
        lieferant: ['AN', 'BEH'],
        wannBenoetigt: 'Für schwimmende Geräte und Arbeitsboote im Einsatz auf Wasserstraßen.',
        sigekoRelevanz: 'Gültigkeit vor dem Einsatz prüfen lassen und zugelassene Personenzahl und Einsatzbereich in den SiGe-Plan übernehmen.',
        rechtsgrundlage: ['BinSchUO'],
        beleg: 'praxis',
        alias: ['Schiffsattest'],
    },

    // ═══ Art C — allgemeine Pflichten (nur Checkliste, nie Vorschlag) ═══════
    {
        id: 'betriebsanleitung',
        name: 'Betriebsanleitung des Herstellers',
        art: 'C',
        gruppe: 'allgemein',
        lieferant: ['AN'],
        wannBenoetigt: 'Für jedes Arbeitsmittel; sie muss am Einsatzort vorliegen und legt bestimmungsgemäße Verwendung, Aufstellung und Wartung fest.',
        sigekoRelevanz: 'Gilt über den Grundsatz-Satz für alle Arbeitsmittel; einzeln anfordern nur, wenn ein Gerät gemeinsam genutzt oder außerhalb des Üblichen eingesetzt wird.',
        rechtsgrundlage: ['BetrSichV § 12 (2)'],
        beleg: 'MK',
        alias: ['Bedienungsanleitung', 'Gebrauchsanleitung'],
    },
    {
        id: 'betriebsanweisung',
        name: 'Betriebsanweisung für Arbeitsmittel',
        art: 'C',
        gruppe: 'allgemein',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Arbeitsmittel vor der ersten Verwendung, in verständlicher Form und Sprache, erstellt vom Arbeitgeber auf Grundlage von Betriebsanleitung und Gefährdungsbeurteilung.',
        sigekoRelevanz: 'Gilt über den Grundsatz-Satz; einzeln anfordern nur bei gemeinsam genutzten Arbeitsmitteln wie Kran, Bauaufzug oder Baustromverteiler.',
        rechtsgrundlage: ['BetrSichV § 12 (2)'],
        beleg: 'MK',
        alias: ['Betriebsanweisung'],
    },
    {
        id: 'pruefnachweis_arbeitsmittel',
        name: 'Prüfnachweise der Arbeitsmittel',
        art: 'C',
        gruppe: 'allgemein',
        lieferant: ['AN'],
        wannBenoetigt: 'Für prüfpflichtige Arbeitsmittel (Leitern, Elektrogeräte, Anschlagmittel, Maschinen); der Nachweis der letzten Prüfung muss am Einsatzort vorliegen.',
        sigekoRelevanz: 'Gilt über den Grundsatz-Satz; stichprobenartig bei Begehungen kontrollieren, besonders bei gemeinsam genutzten Arbeitsmitteln.',
        rechtsgrundlage: ['BetrSichV § 14 (7)'],
        beleg: 'MK',
        alias: ['Prüfnachweis', 'Prüfbuch', 'Prüfprotokoll'],
    },
    {
        id: 'gefaehrdungsbeurteilung',
        name: 'Gefährdungsbeurteilung',
        kurz: 'GB',
        art: 'C',
        gruppe: 'allgemein',
        lieferant: ['AN'],
        wannBenoetigt: 'Für jede Tätigkeit jedes Arbeitgebers vor Beginn der Arbeiten, dokumentiert und bei Änderungen fortgeschrieben.',
        sigekoRelevanz: 'Gilt über den Grundsatz-Satz; der SiGeKo achtet darauf, dass die Gefährdungsbeurteilungen der Gewerke die Wechselwirkungen aus dem SiGe-Plan berücksichtigen, ersetzt sie aber nicht.',
        rechtsgrundlage: ['ArbSchG §§ 5, 6', 'DGUV V 1 § 3'],
        beleg: 'MK',
        alias: ['Gefährdungsbeurteilung des Auftragnehmers'],
    },
    {
        id: 'unterweisungsnachweis',
        name: 'Unterweisungsnachweis',
        art: 'C',
        gruppe: 'allgemein',
        lieferant: ['AN'],
        wannBenoetigt: 'Vor Aufnahme der Tätigkeit und mindestens jährlich, dokumentiert mit Inhalt und Unterschrift.',
        sigekoRelevanz: 'Gilt über den Grundsatz-Satz; die Baustelleneinweisung durch den SiGeKo ergänzt die Unterweisung des Arbeitgebers, ersetzt sie aber nicht.',
        rechtsgrundlage: ['ArbSchG § 12', 'DGUV V 1 § 4 (1)'],
        beleg: 'MK',
    },
    {
        id: 'befaehigungsnachweis',
        name: 'Befähigungs- und Qualifikationsnachweise',
        art: 'C',
        gruppe: 'allgemein',
        lieferant: ['AN'],
        wannBenoetigt: 'Für Tätigkeiten mit besonderer Qualifikation und schriftlicher Beauftragung, etwa Kranführer, Staplerfahrer, Maschinisten, Gerüstbauer, Elektrofachkräfte und Ersthelfer.',
        sigekoRelevanz: 'Gilt über den Grundsatz-Satz; bei koordinationsrelevanten Funktionen (Kranführer, Aufsichtführende, befähigte Personen für gemeinsam genutzte Einrichtungen) die Namen im SiGe-Plan führen.',
        rechtsgrundlage: ['BetrSichV § 12 (3)', 'DGUV V 68 § 7 (1)'],
        beleg: 'MK',
    },
];
